import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Loader2, Plus, Pencil, Trash2, Search, X, EyeOff, Eye, CheckSquare, Square, PackageOpen, ChevronDown, ChevronRight, DollarSign, RefreshCw, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';

const emptyProduct = { title: '', vendor: '', category: '', description: '', price: 0, compare_at_price: null, images: [], tags: [], stock: 0, available: true, featured: false, is_new: false, is_restock: false, bulk_tiers: [] };

const ProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groupByCategory, setGroupByCategory] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredProducts = useMemo(() => {
    if (statusFilter === 'activos') return products.filter((p) => p.available !== false);
    if (statusFilter === 'ocultos') return products.filter((p) => p.available === false);
    return products;
  }, [products, statusFilter]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modal, setModal] = useState(false);
  const [imagesText, setImagesText] = useState('');
  const [tagsText, setTagsText] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkPrice, setBulkPrice] = useState({ open: false, mode: 'fixed', value: '' });
  const [bulkStock, setBulkStock] = useState({ open: false, mode: 'fixed', value: '' });
  const [busyBulk, setBusyBulk] = useState(false);
  const [syncingSheets, setSyncingSheets] = useState(false);

  const syncSheets = async () => {
    setSyncingSheets(true);
    try {
      const r = await api.post('/admin/sync-sheets');
      const { created, updated, errors } = r.data;
      toast({
        title: 'Sincronización completada',
        description: `${created} creados · ${updated} actualizados${errors.length ? ` · ${errors.length} errores` : ''}`,
      });
      if (errors.length) console.warn('Errores sync-sheets:', errors);
      load();
    } catch (e) {
      toast({ title: 'Error al sincronizar', description: e?.response?.data?.detail || 'Intenta de nuevo.' });
    } finally { setSyncingSheets(false); }
  };

  const exportCsv = () => {
    const token = localStorage.getItem('va_token') || '';
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/admin/products/export-csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'productos_nubesmty.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast({ title: 'Error al exportar' }));
  };
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  const [acknowledgedAgotados, setAcknowledgedAgotados] = useState(() => {
    try {
      const saved = localStorage.getItem('nubes_acknowledged_agotados');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleCategoryCollapse = (catName) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(catName)) {
        next.delete(catName);
      } else {
        next.add(catName);
      }
      return next;
    });
  };

  const acknowledgeAgotado = (id) => {
    setAcknowledgedAgotados((prev) => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem('nubes_acknowledged_agotados', JSON.stringify(Array.from(next))); } catch {}
      return next;
    });
  };

  useEffect(() => {
    if (products.length > 0) {
      let changed = false;
      const next = new Set(acknowledgedAgotados);
      products.forEach((p) => {
        if (p.stock > 0 && next.has(p.product_id)) {
          next.delete(p.product_id);
          changed = true;
        }
      });
      if (changed) {
        setAcknowledgedAgotados(next);
        try { localStorage.setItem('nubes_acknowledged_agotados', JSON.stringify(Array.from(next))); } catch {}
      }
    }
  }, [products]);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/products?limit=200${q ? `&q=${encodeURIComponent(q)}` : ''}`),
      api.get('/admin/categories'),
    ]).then(([pr, cr]) => {
      setProducts(pr.data.items);
      setCategories(Array.isArray(cr.data) ? cr.data : []);
      setSelected(new Set());
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const groupedProducts = useMemo(() => {
    if (!groupByCategory) return [{ category: null, items: filteredProducts }];
    const map = new Map();
    filteredProducts.forEach((p) => {
      const key = p.category || 'Sin categoría';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });
    // Order by categories.order asc, then by name; unknown categories at bottom.
    const orderMap = new Map(categories.map((c) => [c.name, c.order ?? 999]));
    return Array.from(map.entries())
      .sort((a, b) => (orderMap.get(a[0]) ?? 998) - (orderMap.get(b[0]) ?? 998) || a[0].localeCompare(b[0]))
      .map(([category, items]) => ({ category, items }));
  }, [filteredProducts, categories, groupByCategory]);

  const allIds = useMemo(() => filteredProducts.map((p) => p.product_id), [filteredProducts]);
  const visibleIds = useMemo(() => {
    if (!groupByCategory) return allIds;
    return groupedProducts
      .filter((g) => g.category === null || !collapsedCategories.has(g.category))
      .flatMap((g) => g.items.map((p) => p.product_id));
  }, [groupedProducts, groupByCategory, collapsedCategories, allIds]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(visibleIds));
  };

  const openNew = () => { setEditing({ ...emptyProduct }); setImagesText(''); setTagsText(''); setModal(true); };
  const openEdit = (p) => {
    setEditing({ ...p });
    setImagesText((p.images || []).join('\n'));
    setTagsText((p.tags || []).join(', '));
    setModal(true);
  };

  const save = async () => {
    const tiersClean = (editing.bulk_tiers || [])
      .map((t) => ({ qty: parseInt(t.qty, 10) || 0, price: Number(t.price) || 0 }))
      .filter((t) => t.qty >= 2 && t.price > 0)
      .sort((a, b) => a.qty - b.qty);
    const payload = {
      ...editing,
      price: Number(editing.price) || 0,
      compare_at_price: editing.compare_at_price ? Number(editing.compare_at_price) : null,
      stock: Number(editing.stock) || 0,
      images: imagesText.split('\n').map((s) => s.trim()).filter(Boolean),
      tags: tagsText.split(',').map((s) => s.trim()).filter(Boolean),
      bulk_tiers: tiersClean,
    };
    try {
      if (editing.product_id) {
        await api.patch(`/admin/products/${editing.product_id}`, payload);
        toast({ title: 'Producto actualizado' });
      } else {
        await api.post('/admin/products', payload);
        toast({ title: 'Producto creado' });
      }
      setModal(false);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se guardó' });
    }
  };

  const remove = async (product_id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    await api.delete(`/admin/products/${product_id}`);
    toast({ title: 'Producto eliminado' });
    load();
  };

  const bulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const labels = { delete: `eliminar ${ids.length} producto${ids.length === 1 ? '' : 's'}`, hide: `ocultar ${ids.length} producto${ids.length === 1 ? '' : 's'}`, show: `mostrar ${ids.length} producto${ids.length === 1 ? '' : 's'}` };
    if (action === 'delete' && !window.confirm(`¿Estás seguro de ${labels.delete}? Esta acción no se puede deshacer.`)) return;
    setBusyBulk(true);
    try {
      const r = await api.post('/admin/products/bulk', { action, ids });
      const doneCount = r.data?.deleted ?? r.data?.updated ?? 0;
      toast({ title: 'Listo', description: `Se aplicó "${labels[action]}" (${doneCount}).` });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo aplicar' });
    } finally {
      setBusyBulk(false);
    }
  };

  const applyBulkStock = async () => {
    const val = parseInt(bulkStock.value);
    if (isNaN(val) || val < 0) return;
    setBusyBulk(true);
    try {
      const r = await api.post('/admin/products/bulk', {
        action: 'set_stock',
        ids: Array.from(selected),
        mode: bulkStock.mode,
        stock: val,
      });
      toast({ title: 'Stock actualizado', description: `${r.data?.updated ?? 0} producto(s) modificados.` });
      setBulkStock({ open: false, mode: 'fixed', value: '' });
      setSelected(new Set());
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo aplicar' });
    } finally { setBusyBulk(false); }
  };

  const applyBulkPrice = async () => {
    const val = parseFloat(bulkPrice.value);
    if (!val || val <= 0) return;
    setBusyBulk(true);
    try {
      const r = await api.post('/admin/products/bulk', {
        action: 'set_price',
        ids: Array.from(selected),
        mode: bulkPrice.mode,
        price: val,
      });
      toast({ title: 'Precios actualizados', description: `${r.data?.updated ?? 0} producto(s) modificados.` });
      setBulkPrice({ open: false, mode: 'fixed', value: '' });
      setSelected(new Set());
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo aplicar' });
    } finally { setBusyBulk(false); }
  };

  const seedVhill = async () => {
    if (!window.confirm('Esto reemplazará TODOS los productos VHILL existentes con el catálogo completo de 68 productos. ¿Continuar?')) return;
    setBusyBulk(true);
    try {
      const r = await api.post('/admin/seed/vhill');
      toast({
        title: 'Catálogo VHILL importado',
        description: `${r.data.products_inserted} productos en 4 categorías. Antiguos borrados: ${r.data.products_deleted}.`,
      });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo importar' });
    } finally {
      setBusyBulk(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-white">Productos</h1>
          <p className="text-white/50 text-sm mt-1">{products.length} productos {selected.size > 0 && <span className="text-orange-400 font-semibold">· {selected.size} seleccionados</span>}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={exportCsv}
            variant="outline"
            className="bg-transparent border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold"
            data-testid="admin-export-csv"
          >
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
          <Button
            onClick={syncSheets}
            disabled={syncingSheets}
            variant="outline"
            className="bg-transparent border-sky-500/40 text-sky-300 hover:bg-sky-500/10 font-semibold"
            data-testid="admin-sync-sheets"
          >
            {syncingSheets ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sincronizar Sheets
          </Button>
          <Button
            onClick={seedVhill}
            disabled={busyBulk}
            variant="outline"
            className="bg-transparent border-orange-500/40 text-orange-300 hover:bg-orange-500/10 font-semibold"
            data-testid="admin-seed-vhill"
          >
            <PackageOpen className="w-4 h-4 mr-1" /> Importar VHILL
          </Button>
          <Button onClick={openNew} className="bg-orange-500 hover:bg-orange-600 text-black font-bold" data-testid="admin-products-new">
            <Plus className="w-4 h-4 mr-1" /> Nuevo
          </Button>
        </div>
      </div>

      <div className="relative mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Buscar por título o marca..." className="pl-9 bg-neutral-950 border-white/10 text-white" />
        </div>
        <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-xl p-1" data-testid="admin-products-status-filter">
          {[
            { id: 'all', label: 'Todos', count: products.length },
            { id: 'activos', label: 'Activos', count: products.filter((p) => p.available !== false).length },
            { id: 'ocultos', label: 'Ocultos', count: products.filter((p) => p.available === false).length },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setStatusFilter(opt.id)}
              data-testid={`admin-products-filter-${opt.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                statusFilter === opt.id
                  ? 'bg-orange-500 text-black shadow'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              {opt.label} <span className={`ml-1 ${statusFilter === opt.id ? 'text-black/60' : 'text-white/30'}`}>{opt.count}</span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm text-white/70 select-none cursor-pointer" data-testid="admin-products-group-toggle">
          <input type="checkbox" checked={groupByCategory} onChange={(e) => setGroupByCategory(e.target.checked)} className="accent-orange-500" />
          Agrupar por categoría
        </label>
      </div>

      {/* Combined Stock Alerts System */}
      {(() => {
        const lowStockProducts = products.filter((p) => p.stock > 0 && p.stock <= 3);
        const agotadosProducts = products.filter((p) => p.stock <= 0 && !acknowledgedAgotados.has(p.product_id));
        if (lowStockProducts.length === 0 && agotadosProducts.length === 0) return null;
        return (
          <div className="mb-5 space-y-3">
            {/* Agotados (Out of Stock) Alerts */}
            {agotadosProducts.length > 0 && (
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-4 text-rose-200 text-sm flex flex-col gap-2" data-testid="agotados-alert-banner">
                <p className="font-bold flex items-center gap-1.5 text-rose-400">
                  <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping"></span>
                  🚨 ¡Alerta de Producto Agotado! {agotadosProducts.length} {agotadosProducts.length === 1 ? 'producto se ha' : 'productos se han'} quedado sin existencias:
                </p>
                <div className="flex flex-col gap-2 mt-1.5 pl-3">
                  {agotadosProducts.slice(0, 5).map((p) => (
                    <div key={p.product_id} className="flex items-center justify-between gap-3 text-xs bg-rose-500/15 border border-rose-500/20 px-3.5 py-2 rounded-xl">
                      <span className="font-semibold text-rose-300">{p.title}</span>
                      <Button
                        onClick={() => {
                          acknowledgeAgotado(p.product_id);
                          toast({ title: 'Alerta omitida', description: 'Se omitió de la lista de alertas activas.' });
                        }}
                        size="sm"
                        variant="ghost"
                        className="h-6 px-3 rounded-full bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-[10px] font-bold uppercase tracking-wider"
                        title="Omitir alerta para este producto agotado"
                        data-testid={`dismiss-agotado-${p.product_id}`}
                      >
                        Entendido / Omitir
                      </Button>
                    </div>
                  ))}
                  {agotadosProducts.length > 5 && (
                    <p className="text-xs text-rose-400/50 italic pl-1">y {agotadosProducts.length - 5} más agotados...</p>
                  )}
                </div>
              </div>
            )}

            {/* Low Stock (Stock Crítico) Alerts */}
            {lowStockProducts.length > 0 && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-4 text-orange-200 text-sm flex flex-col gap-2" data-testid="low-stock-alert-banner">
                <p className="font-bold flex items-center gap-1.5 text-orange-400">
                  <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-ping"></span>
                  ⚠️ ¡Alerta de Stock Crítico! {lowStockProducts.length} {lowStockProducts.length === 1 ? 'producto está' : 'productos están'} por agotarse (stock ≤ 3 unidades):
                </p>
                <div className="flex flex-wrap gap-2 mt-1 pl-3">
                  {lowStockProducts.slice(0, 10).map((p) => (
                    <span key={p.product_id} className="text-xs bg-orange-500/20 px-2.5 py-1 rounded-full border border-orange-500/20 font-semibold text-orange-300">
                      {p.title} ({p.stock} pz.)
                    </span>
                  ))}
                  {lowStockProducts.length > 10 && (
                    <span className="text-xs text-white/40 self-center pl-1">y {lowStockProducts.length - 10} más...</span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 mb-3 rounded-xl bg-orange-500/10 border border-orange-500/30 backdrop-blur-md shadow-lg overflow-hidden" data-testid="admin-products-bulkbar">
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="text-orange-200 text-xs font-bold uppercase tracking-wide">
              {selected.size} seleccionado{selected.size === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
              <Button onClick={() => bulk('show')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-emerald-500/15 hover:!bg-emerald-500/25 border border-emerald-500/30 !text-emerald-300 text-xs font-bold" data-testid="admin-products-bulk-show">
                <Eye className="w-3.5 h-3.5 mr-1" /> Mostrar
              </Button>
              <Button onClick={() => bulk('hide')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-amber-500/15 hover:!bg-amber-500/25 border border-amber-500/30 !text-amber-300 text-xs font-bold" data-testid="admin-products-bulk-hide">
                <EyeOff className="w-3.5 h-3.5 mr-1" /> Ocultar
              </Button>
              <Button
                onClick={() => setBulkPrice((p) => ({ ...p, open: !p.open }))}
                disabled={busyBulk}
                className={`h-8 px-3 rounded-full border text-xs font-bold transition-colors ${bulkPrice.open ? '!bg-blue-500/30 border-blue-400/50 !text-blue-200' : '!bg-blue-500/15 hover:!bg-blue-500/25 border border-blue-500/30 !text-blue-300'}`}
                data-testid="admin-products-bulk-price-toggle"
              >
                <DollarSign className="w-3.5 h-3.5 mr-1" /> Precio
              </Button>
              <Button
                onClick={() => setBulkStock((p) => ({ ...p, open: !p.open }))}
                disabled={busyBulk}
                className={`h-8 px-3 rounded-full border text-xs font-bold transition-colors ${bulkStock.open ? '!bg-teal-500/30 border-teal-400/50 !text-teal-200' : '!bg-teal-500/15 hover:!bg-teal-500/25 border border-teal-500/30 !text-teal-300'}`}
                data-testid="admin-products-bulk-stock-toggle"
              >
                <PackageOpen className="w-3.5 h-3.5 mr-1" /> Stock
              </Button>
              <Button onClick={() => bulk('delete')} disabled={busyBulk} className="h-8 px-3 rounded-full !bg-rose-500/15 hover:!bg-rose-500/25 border border-rose-500/30 !text-rose-300 text-xs font-bold" data-testid="admin-products-bulk-delete">
                {busyBulk ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Eliminar
              </Button>
              <button onClick={() => { setSelected(new Set()); setBulkPrice({ open: false, mode: 'fixed', value: '' }); }} className="h-8 px-2 rounded-full text-white/50 hover:text-white hover:bg-white/10 text-xs" data-testid="admin-products-bulk-clear">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Bulk price panel */}
          {bulkPrice.open && (
            <div className="border-t border-orange-500/20 px-3 py-3 flex flex-wrap items-center gap-2 bg-black/20">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Modificar precio:</span>
              <select
                value={bulkPrice.mode}
                onChange={(e) => setBulkPrice((p) => ({ ...p, mode: e.target.value }))}
                className="h-8 rounded-lg border border-white/20 text-white text-xs px-2 outline-none"
                style={{ background: '#1a1a1a', colorScheme: 'dark' }}
                data-testid="admin-bulk-price-mode"
              >
                <option value="fixed" style={{ background: '#1a1a1a', color: '#fff' }}>Precio fijo ($)</option>
                <option value="increase_pct" style={{ background: '#1a1a1a', color: '#fff' }}>Subir %</option>
                <option value="decrease_pct" style={{ background: '#1a1a1a', color: '#fff' }}>Bajar %</option>
              </select>
              <div className="flex items-center gap-1">
                <span className="text-white/40 text-xs">{bulkPrice.mode === 'fixed' ? '$' : '%'}</span>
                <input
                  type="number"
                  min="0"
                  step={bulkPrice.mode === 'fixed' ? '1' : '0.1'}
                  value={bulkPrice.value}
                  onChange={(e) => setBulkPrice((p) => ({ ...p, value: e.target.value }))}
                  placeholder={bulkPrice.mode === 'fixed' ? 'Ej: 299' : 'Ej: 10'}
                  className="w-28 h-8 rounded-lg bg-white/10 border border-white/20 text-white text-xs px-2 outline-none focus:border-blue-400/50"
                  data-testid="admin-bulk-price-input"
                  onKeyDown={(e) => e.key === 'Enter' && applyBulkPrice()}
                />
              </div>
              <Button
                onClick={applyBulkPrice}
                disabled={busyBulk || !bulkPrice.value}
                className="h-8 px-4 rounded-full bg-blue-500 hover:bg-blue-400 text-white text-xs font-black"
                data-testid="admin-bulk-price-apply"
              >
                {busyBulk ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aplicar'}
              </Button>
              <span className="text-white/35 text-[11px]">a {selected.size} producto{selected.size === 1 ? '' : 's'}</span>
            </div>
          )}

          {/* Bulk stock panel */}
          {bulkStock.open && (
            <div className="border-t border-orange-500/20 px-3 py-3 flex flex-wrap items-center gap-2 bg-black/20">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Modificar stock:</span>
              <select
                value={bulkStock.mode}
                onChange={(e) => setBulkStock((p) => ({ ...p, mode: e.target.value }))}
                className="h-8 rounded-lg border border-white/20 text-white text-xs px-2 outline-none"
                style={{ background: '#1a1a1a', colorScheme: 'dark' }}
                data-testid="admin-bulk-stock-mode"
              >
                <option value="fixed" style={{ background: '#1a1a1a', color: '#fff' }}>Stock fijo</option>
                <option value="add" style={{ background: '#1a1a1a', color: '#fff' }}>Agregar unidades</option>
                <option value="subtract" style={{ background: '#1a1a1a', color: '#fff' }}>Restar unidades</option>
              </select>
              <input
                type="number"
                min="0"
                step="1"
                value={bulkStock.value}
                onChange={(e) => setBulkStock((p) => ({ ...p, value: e.target.value }))}
                placeholder="Ej: 10"
                className="w-28 h-8 rounded-lg bg-white/10 border border-white/20 text-white text-xs px-2 outline-none focus:border-teal-400/50"
                data-testid="admin-bulk-stock-input"
                onKeyDown={(e) => e.key === 'Enter' && applyBulkStock()}
              />
              <Button
                onClick={applyBulkStock}
                disabled={busyBulk || bulkStock.value === ''}
                className="h-8 px-4 rounded-full bg-teal-500 hover:bg-teal-400 text-white text-xs font-black"
                data-testid="admin-bulk-stock-apply"
              >
                {busyBulk ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aplicar'}
              </Button>
              <span className="text-white/35 text-[11px]">a {selected.size} producto{selected.size === 1 ? '' : 's'}</span>
            </div>
          )}
        </div>
      )}

      {loading ? <Loader2 className="w-6 h-6 animate-spin text-orange-500" /> : (
        <div className="bg-neutral-950 border border-white/10 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-white/5 text-white/60">
              <tr>
                <th className="text-left px-3 py-2 w-10">
                  <button
                    type="button"
                    onClick={toggleAll}
                    aria-label="Seleccionar todo"
                    className="inline-flex items-center justify-center w-5 h-5 rounded border border-white/20 hover:border-orange-400/60 hover:bg-orange-500/10 text-orange-300"
                    data-testid="admin-products-select-all"
                  >
                    {allSelected ? <CheckSquare className="w-4 h-4" /> : someSelected ? <span className="w-2.5 h-0.5 bg-orange-400 rounded"></span> : <Square className="w-4 h-4 text-white/25" />}
                  </button>
                </th>
                <th className="text-left px-3 py-2">Imagen</th>
                <th className="text-left px-3 py-2">Título</th>
                <th className="text-left px-3 py-2">Marca</th>
                <th className="text-left px-3 py-2">Categoría</th>
                <th className="text-right px-3 py-2">Precio</th>
                <th className="text-right px-3 py-2">Stock</th>
                <th className="text-right px-3 py-2 w-24">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {groupedProducts.map((group, gi) => {
                const rows = [];
                const isCollapsed = group.category !== null && collapsedCategories.has(group.category);
                
                if (groupByCategory && group.category !== null) {
                  rows.push(
                    <tr
                      key={`grp-${group.category}-${gi}`}
                      onClick={() => toggleCategoryCollapse(group.category)}
                      className="bg-orange-500/[0.06] hover:bg-orange-500/[0.12] border-t border-orange-500/20 sticky cursor-pointer transition-colors select-none"
                      data-testid={`admin-product-category-${group.category}`}
                    >
                      <td colSpan={8} className="px-3 py-2 text-orange-300 font-black uppercase tracking-wider text-[11px]">
                        <div className="flex items-center gap-1.5">
                          {isCollapsed ? <ChevronRight className="w-4 h-4 text-orange-400 shrink-0" /> : <ChevronDown className="w-4 h-4 text-orange-400 shrink-0" />}
                          <span className="inline-block w-2 h-4 rounded-sm bg-orange-500 align-middle"></span>
                          <span>{group.category}</span>
                          <span className="ml-2 text-white/40 font-semibold normal-case tracking-normal">· {group.items.length} {group.items.length === 1 ? 'producto' : 'productos'}</span>
                          {isCollapsed && <span className="ml-auto text-[10px] text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded-full font-bold">Contraído</span>}
                        </div>
                      </td>
                    </tr>
                  );
                }
                
                if (!isCollapsed) {
                  group.items.forEach((p) => {
                    const isSel = selected.has(p.product_id);
                    rows.push(
                    <tr key={p.product_id} className={`border-t border-white/5 hover:bg-white/5 ${isSel ? 'bg-orange-500/[0.06]' : ''}`}>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => toggleOne(p.product_id)}
                          aria-label={`Seleccionar ${p.title}`}
                          className={`inline-flex items-center justify-center w-5 h-5 rounded border transition-colors ${isSel ? 'border-orange-400 bg-orange-500 text-black' : 'border-white/20 hover:border-orange-400/60 hover:bg-orange-500/10 text-white/40'}`}
                          data-testid={`admin-product-select-${p.product_id}`}
                        >
                          {isSel ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="px-3 py-2"><div className="w-12 h-12 rounded bg-neutral-900 overflow-hidden">{p.main_image && <img src={p.main_image} alt="" className="w-full h-full object-cover" />}</div></td>
                      <td className="px-3 py-2 text-white max-w-xs truncate uppercase">
                        {p.title}
                        {p.available === false && <span className="ml-2 text-[10px] text-amber-300 font-bold uppercase tracking-wider">Oculto</span>}
                        {p.stock <= 0 && <span className="ml-2 text-[10px] text-rose-400 font-bold uppercase tracking-wider">Sin Stock (Oculto)</span>}
                      </td>
                      <td className="px-3 py-2 text-white/70">{p.vendor}</td>
                      <td className="px-3 py-2 text-white/70">{p.category}</td>
                      <td className="px-3 py-2 text-right text-white">${p.price}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${p.stock <= 0 ? 'text-rose-500' : p.stock <= 3 ? 'text-amber-500' : 'text-white/60'}`}>
                        {p.stock <= 0 ? 'Agotado' : `${p.stock} pz.`}
                      </td>
                      <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                        <button
                          onClick={async () => {
                            const newAvailable = p.available !== false ? false : true;
                            await api.patch(`/admin/products/${p.product_id}`, { ...p, available: newAvailable });
                            toast({ title: newAvailable ? 'Producto visible' : 'Producto ocultado' });
                            load();
                          }}
                          className={`w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors ${p.available !== false ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20'}`}
                          title={p.available !== false ? 'Ocultar producto de la tienda' : 'Mostrar producto en la tienda'}
                        >
                          {p.available !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => openEdit(p)} className="w-8 h-8 rounded-md bg-white/5 hover:bg-white/10 text-white inline-flex items-center justify-center" title="Editar"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(p.product_id)} className="w-8 h-8 rounded-md bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 inline-flex items-center justify-center" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  );
                });
                }
                return rows;
              })}
              {products.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-white/50">Sin productos. Añade uno con "Nuevo" o importa VHILL.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="sm:max-w-2xl bg-neutral-950 border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editing?.product_id ? 'Editar producto' : 'Nuevo producto'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-white/60">Título</label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Marca</label>
                <Input value={editing.vendor} onChange={(e) => setEditing({ ...editing, vendor: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Categoría</label>
                {categories.length > 0 ? (
                  <div className="space-y-1">
                    <select
                      value={categories.some((c) => c.name === editing.category) ? editing.category : ''}
                      onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                      className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm focus:outline-none focus:border-orange-500/40"
                      data-testid="admin-product-category-select"
                    >
                      <option value="" className="bg-neutral-950">— Selecciona una categoría —</option>
                      {categories.map((c) => (
                        <option key={c.slug} value={c.name} className="bg-neutral-950">
                          {c.name}{c.visible === false ? ' (oculta)' : ''}
                        </option>
                      ))}
                    </select>
                    {editing.category && !categories.some((c) => c.name === editing.category) && (
                      <p className="text-[10px] text-amber-400">Categoría actual "{editing.category}" no existe. Elige una del listado o créala en la sección Categorías.</p>
                    )}
                  </div>
                ) : (
                  <Input
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="bg-white/5 border-white/10 text-white"
                    placeholder="Crea categorías primero"
                  />
                )}
              </div>
              <div>
                <label className="text-xs text-white/60">Precio (MXN)</label>
                <Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Precio antes</label>
                <Input type="number" value={editing.compare_at_price ?? ''} onChange={(e) => setEditing({ ...editing, compare_at_price: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-xs text-white/60">Stock</label>
                <Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="flex flex-wrap items-end gap-3">
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
                  <input type="checkbox" checked={editing.available} onChange={(e) => setEditing({ ...editing, available: e.target.checked })} className="accent-orange-500" /> Disponible
                </label>
                <label className="flex items-center gap-2 text-sm text-white/80 cursor-pointer select-none">
                  <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-orange-500" /> Destacado
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none" title="Muestra etiqueta naranja 'Nuevo' en la tarjeta">
                  <input type="checkbox" checked={!!editing.is_new} onChange={(e) => setEditing({ ...editing, is_new: e.target.checked })} className="accent-orange-500" />
                  <span className="text-orange-400 font-bold">Nuevo</span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none" title="Muestra etiqueta amarilla 'Próximamente' en la tarjeta">
                  <input type="checkbox" checked={!!editing.is_restock} onChange={(e) => setEditing({ ...editing, is_restock: e.target.checked })} className="accent-yellow-400" />
                  <span className="text-yellow-400 font-bold">En restock</span>
                </label>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60">Imágenes (una URL por línea)</label>
                <Textarea value={imagesText} onChange={(e) => setImagesText(e.target.value)} rows={3} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60">Tags (separadas por coma)</label>
                <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="col-span-2 rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-white">Precio por cantidad (bulk)</div>
                    <div className="text-[11px] text-white/50">Ej. 3 unidades por $1500. Se aplica automáticamente al carrito.</div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setEditing({ ...editing, bulk_tiers: [...(editing.bulk_tiers || []), { qty: 2, price: '' }] })}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
                    data-testid="admin-product-add-tier"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Añadir tier
                  </Button>
                </div>
                {(editing.bulk_tiers || []).length === 0 ? (
                  <div className="text-xs text-white/40 italic">Sin descuentos por cantidad.</div>
                ) : (
                  <div className="space-y-2">
                    {(editing.bulk_tiers || []).map((tier, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`admin-product-tier-${idx}`}>
                        <Input
                          type="number" min={2}
                          value={tier.qty}
                          onChange={(e) => {
                            const next = [...editing.bulk_tiers];
                            next[idx] = { ...next[idx], qty: e.target.value };
                            setEditing({ ...editing, bulk_tiers: next });
                          }}
                          className="bg-white/5 border-white/10 text-white w-24"
                          placeholder="Cant."
                        />
                        <span className="text-white/60 text-sm">unidades por</span>
                        <span className="text-white/60 text-sm">$</span>
                        <Input
                          type="number" min={0} step="0.01"
                          value={tier.price}
                          onChange={(e) => {
                            const next = [...editing.bulk_tiers];
                            next[idx] = { ...next[idx], price: e.target.value };
                            setEditing({ ...editing, bulk_tiers: next });
                          }}
                          className="bg-white/5 border-white/10 text-white w-32"
                          placeholder="Total"
                        />
                        <span className="text-white/40 text-xs">MXN total</span>
                        <Button
                          type="button" size="sm" variant="outline"
                          onClick={() => setEditing({ ...editing, bulk_tiers: editing.bulk_tiers.filter((_, i) => i !== idx) })}
                          className="ml-auto bg-transparent border-red-500/40 text-red-400 hover:bg-red-500/10"
                          data-testid={`admin-product-tier-remove-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <label className="text-xs text-white/60">Descripción</label>
                <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={4} className="bg-white/5 border-white/10 text-white" />
              </div>
              <div className="col-span-2 flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setModal(false)} className="bg-transparent border-white/20 text-white hover:bg-white/10"><X className="w-4 h-4 mr-1" /> Cancelar</Button>
                <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-black font-bold">Guardar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsSection;
